import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Search, RefreshCw, Inbox, CheckCircle2, ListChecks } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { retentionAPI } from '@/lib/api';
import OpportunityCard from './OpportunityCard';
import RetentionAIAssistant from './RetentionAIAssistant';

const EMPTY_COPY = {
  renewals: 'No renewals or reviews due right now. They appear automatically as enrollment seasons approach.',
  cross_sell: 'No cross-sell opportunities found. Active clients with product gaps appear here.',
  needs_contact: 'Everyone has been contacted recently. Clients past the no-contact threshold appear here.',
  at_risk: 'No at-risk clients right now. Long silences and stale renewals appear here.',
};

// Agent workspace for one retention category (renewals / cross-sell /
// needs-contact / at-risk). Compact, action-oriented list of opportunities.
export default function RetentionView({ category, onChanged }) {
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [aiOpp, setAiOpp] = useState(null);
  const [showWorked, setShowWorked] = useState(false);

  const load = useCallback(async (search, worked) => {
    setLoading(true);
    try {
      if (worked) {
        const r = await retentionAPI.listWorked(category);
        setOpps(r.data?.worked || []);
      } else {
        const r = await retentionAPI.opportunities(category, search);
        setOpps(r.data?.opportunities || []);
      }
    } catch {
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => { setShowWorked(false); setQ(''); load('', false); }, [load]);

  const toggleWorked = () => {
    const next = !showWorked;
    setShowWorked(next);
    load(q, next);
  };

  // Optimistically drop a card the moment it is marked worked / undone.
  const removeCard = (o) => {
    setOpps((prev) => prev.filter((x) => (x.worked_id || x.id) !== (o.worked_id || o.id)));
    onChanged && onChanged();
  };

  return (
    <div className="space-y-3" data-testid={`retention-view-${category}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load(q, showWorked)}
            placeholder="Search clients…"
            className="pl-9 h-9"
            data-testid="retention-search"
            disabled={showWorked}
          />
        </div>
        <Button variant="outline" size="sm" className="h-9" onClick={() => load(q, showWorked)} data-testid="retention-refresh">
          <RefreshCw size={14} className="mr-1.5" /> Refresh
        </Button>
        <Button
          variant={showWorked ? 'default' : 'outline'}
          size="sm"
          className={`h-9 ${showWorked ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
          onClick={toggleWorked}
          data-testid="retention-toggle-worked"
        >
          {showWorked ? <ListChecks size={14} className="mr-1.5" /> : <CheckCircle2 size={14} className="mr-1.5" />}
          {showWorked ? 'Active list' : 'Worked'}
        </Button>
        <span className="ml-auto text-sm text-slate-500" data-testid="retention-count">
          {opps.length} {showWorked ? 'worked' : (opps.length === 1 ? 'opportunity' : 'opportunities')}
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : opps.length === 0 ? (
        <div className="text-center py-16 text-slate-500" data-testid="retention-empty">
          <Inbox className="h-10 w-10 mx-auto text-slate-300 mb-2" />
          <p className="max-w-md mx-auto text-sm">
            {showWorked ? 'Nothing marked as worked this season yet. Use "Mark as Worked" on a card to clear it from the list.' : EMPTY_COPY[category]}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {opps.map((o) => (
            <OpportunityCard
              key={o.worked_id || o.id}
              opp={o}
              worked={showWorked}
              onAI={setAiOpp}
              onWorked={removeCard}
              onUndo={removeCard}
            />
          ))}
        </div>
      )}

      <RetentionAIAssistant open={!!aiOpp} opportunity={aiOpp} onClose={() => setAiOpp(null)} />
    </div>
  );
}
